import React, { useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Point, OriginalLine, VertebraType, AnnotationMode, OBBAnnotations, ViewAngleType } from '../../types';

interface AnnotationCanvasProps {
  image: HTMLImageElement | null;
  annotations: Record<string, any>;
  originalLines: OriginalLine[];
  currentTriangle: Point[];
  currentVertebra: VertebraType;
  currentEdge: 'upper' | 'lower';
  showOriginalLines: boolean;
  originalLinesOpacity: number;
  triangleOpacity: number;
  onCanvasClick: (point: Point) => void;
  // New OBB mode props
  annotationMode: AnnotationMode;
  obbAnnotations: OBBAnnotations;
  currentQuadrilateral: Point[];
  currentViewAngle: ViewAngleType;
  quadrilateralOpacity: number;
  processedVertebrae: string[];
}

export interface AnnotationCanvasRef {
  redraw: () => void;
}

const VERTEBRA_COLORS: Record<VertebraType, string> = {
  L1: '#FF6B6B',
  L2: '#4ECDC4',
  L3: '#45B7B8',
  L4: '#96CEB4',
  L5: '#FFEAA7'
};

const VERTEBRA_LABELS = [
  'L1a', 'L1b', 'L2a', 'L2b', 'L3a', 'L3b',
  'L4a', 'L4b', 'L5a', 'L5b', 'S1a'
];

export const AnnotationCanvas = forwardRef<AnnotationCanvasRef, AnnotationCanvasProps>(
  ({ 
    image, 
    annotations, 
    originalLines, 
    currentTriangle, 
    currentVertebra, 
    currentEdge, 
    showOriginalLines,
    originalLinesOpacity,
    triangleOpacity,
    onCanvasClick,
    // New OBB mode props
    annotationMode,
    obbAnnotations,
    currentQuadrilateral,
    currentViewAngle,
    quadrilateralOpacity,
    processedVertebrae
  }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const displayScaleRef = useRef<number>(1);
    const zoomScaleRef = useRef<number>(1);
    const panOffsetRef = useRef<Point>({ x: 0, y: 0 });
    const isDraggingRef = useRef<boolean>(false);
    const lastMousePosRef = useRef<Point>({ x: 0, y: 0 });
    const [zoomDisplay, setZoomDisplay] = React.useState(100);

    useImperativeHandle(ref, () => ({
      redraw
    }));

    const setupCanvas = () => {
      if (!canvasRef.current || !image || !containerRef.current) return;

      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      const scaleByWidth = containerWidth / image.width;
      const scaleByHeight = containerHeight / image.height;
      let scale = Math.min(scaleByWidth, scaleByHeight) * 0.9; // 留一些邊距
      
      scale = Math.max(scale, 0.1);
      scale = Math.min(scale, 3); // 允許放大到3倍
      
      canvas.width = image.width;
      canvas.height = image.height;
      
      displayScaleRef.current = scale;
      zoomScaleRef.current = 1; // 重置縮放
      panOffsetRef.current = { x: 0, y: 0 }; // 重置平移
      
      updateCanvasTransform();
      redraw();
    };

    const updateCanvasTransform = () => {
      if (!canvasRef.current) return;
      
      const canvas = canvasRef.current;
      const totalScale = displayScaleRef.current * zoomScaleRef.current;
      const displayWidth = canvas.width * totalScale;
      const displayHeight = canvas.height * totalScale;
      
      canvas.style.width = displayWidth + 'px';
      canvas.style.height = displayHeight + 'px';
      canvas.style.transform = `translate(${panOffsetRef.current.x}px, ${panOffsetRef.current.y}px)`;
    };

    const redraw = useCallback(() => {
      if (!canvasRef.current || !image) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      if (showOriginalLines && originalLines.length > 0) {
        drawOriginalLines(ctx);
      }

      if (annotationMode === 'triangle') {
        drawTriangles(ctx);
        if (currentTriangle.length > 0) {
          drawPartialTriangle(ctx);
        }
      } else if (annotationMode === 'obb') {
        drawOBBQuadrilaterals(ctx);
        if (currentQuadrilateral.length > 0) {
          drawPartialQuadrilateral(ctx);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      image,
      showOriginalLines,
      originalLines,
      originalLinesOpacity,
      annotationMode,
      annotations,
      currentTriangle,
      triangleOpacity,
      obbAnnotations,
      currentQuadrilateral,
      quadrilateralOpacity
    ]);

    const drawOriginalLines = (ctx: CanvasRenderingContext2D) => {
      ctx.strokeStyle = `rgba(255, 102, 0, ${originalLinesOpacity})`;
      ctx.lineWidth = 8;
      ctx.setLineDash([20, 12]);

      originalLines.forEach((line, index) => {
        ctx.beginPath();
        ctx.moveTo(line.x1, line.y1);
        ctx.lineTo(line.x2, line.y2);
        ctx.stroke();

        ctx.fillStyle = `rgba(255, 102, 0, ${originalLinesOpacity})`;
        ctx.beginPath();
        ctx.arc(line.x1, line.y1, 12, 0, 2 * Math.PI);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(line.x2, line.y2, 12, 0, 2 * Math.PI);
        ctx.fill();

        const midX = (line.x1 + line.x2) / 2;
        const midY = (line.y1 + line.y2) / 2;
        
        ctx.fillStyle = `rgba(255, 255, 255, ${originalLinesOpacity * 0.95})`;
        ctx.font = 'bold 24px Arial';
        const label = VERTEBRA_LABELS[index] || `Line ${index + 1}`;
        const metrics = ctx.measureText(label);
        ctx.fillRect(midX - metrics.width/2 - 8, midY - 30, metrics.width + 16, 32);
        
        ctx.fillStyle = `rgba(255, 102, 0, ${originalLinesOpacity})`;
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(label, midX, midY - 8);
      });

      ctx.setLineDash([]);
    };

    const drawTriangles = (ctx: CanvasRenderingContext2D) => {
      Object.entries(annotations).forEach(([vertebra, vertebraData]) => {
        const color = VERTEBRA_COLORS[vertebra as VertebraType];
        
        if (vertebraData.upper) {
          drawTriangle(ctx, vertebraData.upper.points, color, `${vertebra}a`, triangleOpacity);
        }
        
        if (vertebraData.lower) {
          drawTriangle(ctx, vertebraData.lower.points, color, `${vertebra}b`, triangleOpacity);
        }
      });
    };

    const drawTriangle = (ctx: CanvasRenderingContext2D, points: Point[], color: string, label: string, opacity: number = 1) => {
      if (points.length !== 3) return;

      // 將顏色轉換為帶透明度的版本
      const hexToRgba = (hex: string, alpha: number): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const strokeColor = color.startsWith('#') 
        ? hexToRgba(color, opacity)
        : color.includes('rgba') 
          ? color.replace(/rgba?\([^)]*\)/, (match) => match.replace(/[\d.]+(?=\))/, opacity.toString()))
          : color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
      
      // 內部填滿透明度為設定值-0.2，最小為0.1
      const fillOpacity = Math.max(0.1, opacity - 0.2);
      const fillColor = color.startsWith('#')
        ? hexToRgba(color, fillOpacity)
        : color.includes('rgba')
          ? color.replace(/rgba?\([^)]*\)/, (match) => match.replace(/[\d.]+(?=\))/, fillOpacity.toString()))
          : color.replace('rgb', 'rgba').replace(')', `, ${fillOpacity})`);

      ctx.strokeStyle = strokeColor;
      ctx.fillStyle = fillColor;
      ctx.lineWidth = 8;

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(points[1].x, points[1].y);
      ctx.lineTo(points[2].x, points[2].y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      points.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 16, 0, 2 * Math.PI);
        ctx.fillStyle = strokeColor;
        ctx.fill();
        
        ctx.strokeStyle = `rgba(0, 0, 0, ${opacity})`;
        ctx.lineWidth = 4;
        ctx.stroke();
        
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.font = 'bold 22px Arial';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 8);
      });

      const centerX = (points[0].x + points[1].x + points[2].x) / 3;
      const centerY = (points[0].y + points[1].y + points[2].y) / 3;
      
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.font = 'bold 28px Arial';
      const metrics = ctx.measureText(label);
      ctx.fillRect(centerX - metrics.width/2 - 12, centerY - 36, metrics.width + 24, 36);
      
      ctx.strokeStyle = `rgba(0, 0, 0, ${opacity})`;
      ctx.lineWidth = 3;
      ctx.strokeRect(centerX - metrics.width/2 - 12, centerY - 36, metrics.width + 24, 36);
      
      ctx.fillStyle = strokeColor;
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(label, centerX, centerY - 8);
    };

    const drawPartialTriangle = (ctx: CanvasRenderingContext2D) => {
      const color = VERTEBRA_COLORS[currentVertebra];
      ctx.strokeStyle = color;
      ctx.lineWidth = 6;
      ctx.setLineDash([12, 8]);

      currentTriangle.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 14, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.stroke();
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 6);

        ctx.strokeStyle = color;
        ctx.lineWidth = 6;
        ctx.setLineDash([12, 8]);

        if (index > 0) {
          ctx.beginPath();
          ctx.moveTo(currentTriangle[index - 1].x, currentTriangle[index - 1].y);
          ctx.lineTo(point.x, point.y);
          ctx.stroke();
        }
      });

      if (currentTriangle.length === 2) {
        ctx.beginPath();
        ctx.moveTo(currentTriangle[1].x, currentTriangle[1].y);
        ctx.lineTo(currentTriangle[0].x, currentTriangle[0].y);
        ctx.stroke();
      }

      ctx.setLineDash([]);
    };

    // New OBB mode drawing functions
    const drawOBBQuadrilaterals = (ctx: CanvasRenderingContext2D) => {
      Object.entries(obbAnnotations).forEach(([vertebraId, annotation]) => {
        drawQuadrilateral(ctx, annotation.quadrilateral.points, getVertebraColor(vertebraId), vertebraId, annotation.viewAngle, quadrilateralOpacity);
      });
    };

    const drawQuadrilateral = (ctx: CanvasRenderingContext2D, points: Point[], color: string, label: string, viewAngle: string, opacity: number = 1) => {
      if (points.length !== 4) return;

      const hexToRgba = (hex: string, alpha: number): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const strokeColor = color.startsWith('#') 
        ? hexToRgba(color, opacity)
        : color.includes('rgba') 
          ? color.replace(/rgba?\([^)]*\)/, (match) => match.replace(/[\d.]+(?=\))/, opacity.toString()))
          : color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);

      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 6; // 統一使用6px邊框寬度
      ctx.setLineDash([]); // 移除虛線，全部使用實線

      // Draw quadrilateral - 只畫邊框，不填充
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(points[1].x, points[1].y);
      ctx.lineTo(points[2].x, points[2].y);
      ctx.lineTo(points[3].x, points[3].y);
      ctx.closePath();
      ctx.stroke();

      // Draw corner points
      points.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 14, 0, 2 * Math.PI);
        ctx.fillStyle = strokeColor;
        ctx.fill();
        
        ctx.strokeStyle = `rgba(0, 0, 0, ${opacity})`;
        ctx.lineWidth = 3;
        ctx.setLineDash([]);
        ctx.stroke();
        
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 6);
      });

      // Draw label in center
      const centerX = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
      const centerY = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;
      
      const labelText = `${label} - ${viewAngle}`;
      
      // 根據視角設定不同的背景顏色和文字樣式
      let bgColor: string;
      let textColor: string;
      let fontWeight: string;
      
      switch (viewAngle) {
        case '俯視':
          bgColor = `rgba(59, 130, 246, ${opacity * 0.9})`; // 藍色背景
          textColor = 'rgba(255, 255, 255, 1)'; // 白色文字
          fontWeight = 'bold';
          break;
        case '正視':
          bgColor = `rgba(34, 197, 94, ${opacity * 0.9})`; // 深綠色背景
          textColor = 'rgba(255, 255, 255, 1)'; // 白色文字
          fontWeight = 'bold';
          break;
        case '仰視':
          bgColor = `rgba(245, 158, 11, ${opacity * 0.9})`; // 橙色背景
          textColor = 'rgba(0, 0, 0, 1)'; // 黑色文字 (橙色背景配黑字更清楚)
          fontWeight = 'bold';
          break;
        default:
          bgColor = `rgba(255, 255, 255, ${opacity})`;
          textColor = strokeColor;
          fontWeight = 'bold';
      }
      
      ctx.fillStyle = bgColor;
      ctx.font = `${fontWeight} 24px Arial`;
      const metrics = ctx.measureText(labelText);
      const padding = 12;
      const boxHeight = 32;
      ctx.fillRect(centerX - metrics.width/2 - padding, centerY - boxHeight, metrics.width + padding * 2, boxHeight);
      
      ctx.strokeStyle = `rgba(0, 0, 0, ${opacity * 0.8})`;
      ctx.lineWidth = 2;
      ctx.strokeRect(centerX - metrics.width/2 - padding, centerY - boxHeight, metrics.width + padding * 2, boxHeight);
      
      ctx.fillStyle = textColor;
      ctx.font = `${fontWeight} 24px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(labelText, centerX, centerY - 6);

      ctx.setLineDash([]);
    };

    const drawPartialQuadrilateral = (ctx: CanvasRenderingContext2D) => {
      if (currentQuadrilateral.length === 0) return;

      ctx.strokeStyle = '#FF6B6B';
      ctx.lineWidth = 4;
      ctx.setLineDash([8, 6]);

      currentQuadrilateral.forEach((point, index) => {
        // Draw point
        ctx.beginPath();
        ctx.arc(point.x, point.y, 12, 0, 2 * Math.PI);
        ctx.fillStyle = '#FF6B6B';
        ctx.fill();
        
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.stroke();
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText((index + 1).toString(), point.x, point.y + 5);

        // Draw lines to previous points
        ctx.strokeStyle = '#FF6B6B';
        ctx.lineWidth = 4;
        ctx.setLineDash([8, 6]);

        if (index > 0) {
          ctx.beginPath();
          ctx.moveTo(currentQuadrilateral[index - 1].x, currentQuadrilateral[index - 1].y);
          ctx.lineTo(point.x, point.y);
          ctx.stroke();
        }
      });

      // Close the quadrilateral if we have 4 points
      if (currentQuadrilateral.length === 3) {
        ctx.beginPath();
        ctx.moveTo(currentQuadrilateral[2].x, currentQuadrilateral[2].y);
        ctx.lineTo(currentQuadrilateral[0].x, currentQuadrilateral[0].y);
        ctx.stroke();
      }

      ctx.setLineDash([]);
    };

    const getVertebraColor = (vertebraId: string): string => {
      // Extract vertebra level (L1, L2, etc.) from vertebraId
      const level = vertebraId.replace(/[ab]$/i, '') as VertebraType;
      return VERTEBRA_COLORS[level] || '#888888';
    };

    const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current || !image || isDraggingRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const totalScale = displayScaleRef.current * zoomScaleRef.current;
      const x = (e.clientX - rect.left) / totalScale;
      const y = (e.clientY - rect.top) / totalScale;

      onCanvasClick({ x, y });
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if ((e.button === 0 && (e.ctrlKey || e.metaKey)) || e.button === 1) { // Ctrl+左鍵 或 中鍵
        isDraggingRef.current = true;
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isDraggingRef.current) {
        const deltaX = e.clientX - lastMousePosRef.current.x;
        const deltaY = e.clientY - lastMousePosRef.current.y;
        
        panOffsetRef.current = {
          x: panOffsetRef.current.x + deltaX,
          y: panOffsetRef.current.y + deltaY
        };
        
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        updateCanvasTransform();
        e.preventDefault();
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    useEffect(() => {
      if (image) {
        const timer = setTimeout(setupCanvas, 100);
        return () => clearTimeout(timer);
      }
    }, [image]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      redraw();
    }, [
      annotations, 
      originalLines, 
      currentTriangle, 
      showOriginalLines, 
      originalLinesOpacity, 
      triangleOpacity,
      // OBB mode dependencies
      annotationMode,
      obbAnnotations,
      currentQuadrilateral,
      quadrilateralOpacity,
      processedVertebrae,
      redraw
    ]);

    // 使用原生事件監聽器來處理Canvas上的滾輪事件
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas || !image) return;

      const handleNativeWheel = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoomScale = Math.max(0.2, Math.min(5, zoomScaleRef.current * zoomFactor));
        
        // 計算縮放中心點調整 - 修正版本
        // 首先計算鼠標位置相對於容器中心的偏移
        const containerCenterX = rect.width / 2;
        const containerCenterY = rect.height / 2;
        
        // 計算鼠標位置相對於當前Canvas中心的偏移
        const offsetFromCenterX = mouseX - containerCenterX;
        const offsetFromCenterY = mouseY - containerCenterY;
        
        // 計算縮放比例變化
        const scaleChange = newZoomScale / zoomScaleRef.current;
        
        // 調整平移偏移量，使縮放以鼠標位置為中心
        const newPanX = panOffsetRef.current.x - offsetFromCenterX * (scaleChange - 1);
        const newPanY = panOffsetRef.current.y - offsetFromCenterY * (scaleChange - 1);
        
        zoomScaleRef.current = newZoomScale;
        panOffsetRef.current = { x: newPanX, y: newPanY };
        
        setZoomDisplay(Math.round(newZoomScale * 100));
        updateCanvasTransform();
      };

      // 添加passive: false以允許preventDefault
      canvas.addEventListener('wheel', handleNativeWheel, { passive: false });

      return () => {
        canvas.removeEventListener('wheel', handleNativeWheel);
      };
    }, [image]);

    return (
      <div 
        ref={containerRef}
        className="canvas-container"
        style={{ 
          display: image ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {!image && (
          <div style={{ 
            color: '#718096', 
            fontSize: '1.2rem', 
            textAlign: 'center',
            padding: '40px' 
          }}>
            請選擇影像檔案開始標記
          </div>
        )}
        
        {image && (
          <>
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                cursor: isDraggingRef.current ? 'grabbing' : 'crosshair',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                transformOrigin: 'top left'
              }}
            />
            
            <div style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '0.9rem',
              pointerEvents: 'none'
            }}>
              縮放: {zoomDisplay}%
              <br />
              <small>滾輪縮放 | Ctrl+拖拽平移</small>
            </div>
          </>
        )}
      </div>
    );
  }
);